import React from "react";
import {Link} from "react-router-dom";
import StatusBadge from "../components/StatusBadge";
import {LEAD_KIND} from "./LeadsTab";

const Stat = ({value, label, tone, onClick}) => (
    <button type="button" className={`brk-stat${tone ? ` brk-stat--${tone}` : ""}`} onClick={onClick}>
        <span className="brk-stat__value">{value ?? 0}</span>
        <span className="brk-stat__label">{label}</span>
    </button>
);

/** At a glance: numbers, what needs the broker's attention, and the latest leads. */
const OverviewTab = ({broker, stats, listings, projects, leads, onGo, approved}) => {
    const attention = [
        ...projects.filter((p) => ["DRAFT", "CHANGES_REQUESTED"].includes(p.reviewStatus))
            .map((p) => ({key: `p${p.id}`, title: p.name, status: p.reviewStatus, note: p.reviewNote, to: `/broker/projects/${p.id}`})),
        ...listings.filter((l) => !l.projectId && ["DRAFT", "CHANGES_REQUESTED"].includes(l.reviewStatus))
            .map((l) => ({key: `l${l.id}`, title: l.title, status: l.reviewStatus, note: l.reviewNote, to: `/broker/listings/${l.id}`})),
    ];
    const empty = listings.length === 0 && projects.length === 0;
    const isBuilder = broker.accountType === "BUILDER";

    return (
        <div className="brk-overview">
            <div className="brk-stats">
                <Stat value={stats?.activeListings} label="Live listings" tone="green" onClick={() => onGo("listings")}/>
                <Stat value={stats?.inReview} label="In review" tone="amber" onClick={() => onGo("listings")}/>
                <Stat value={stats?.needsAttention} label="Need your attention" tone={stats?.needsAttention ? "red" : ""}
                      onClick={() => onGo("listings")}/>
                <Stat value={stats?.projects} label="Projects" onClick={() => onGo("projects")}/>
                <Stat value={stats?.newLeads} label="New leads" tone={stats?.newLeads ? "orange" : ""} onClick={() => onGo("leads")}/>
                <Stat value={stats?.leadsLast30Days} label="Leads, last 30 days" onClick={() => onGo("leads")}/>
            </div>

            {empty && approved && (
                <div className="brk-card brk-getting-started">
                    <h2>Get your first {isBuilder ? "project" : "listing"} live</h2>
                    <ol>
                        <li>{isBuilder ? "Create a project with its photos, RERA number and amenities." : "Add a listing with photos, price and details."}</li>
                        {isBuilder && <li>Add each unit type (2 BHK, 3 BHK…) with its size and price.</li>}
                        <li>Submit it for review. Our team checks it, usually within a working day.</li>
                        <li>Once approved it appears in search and in our AI assistant, and buyers' enquiries come to you here and by email.</li>
                    </ol>
                    <div className="brk-actions">
                        {isBuilder && <Link className="brk-btn brk-btn--primary" to="/broker/projects/new">Create a project</Link>}
                        <Link className={`brk-btn ${isBuilder ? "brk-btn--ghost" : "brk-btn--primary"}`} to="/broker/listings/new">Add a single listing</Link>
                    </div>
                </div>
            )}

            {attention.length > 0 && (
                <div className="brk-card">
                    <h2 className="brk-card__title">Needs your attention</h2>
                    <ul className="brk-list">
                        {attention.map((a) => (
                            <li key={a.key} className="brk-list__row">
                                <div>
                                    <Link to={a.to} className="brk-list__title">{a.title}</Link>
                                    {a.note && <p className="brk-list__note">Reviewer: {a.note}</p>}
                                </div>
                                <StatusBadge status={a.status}/>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            <div className="brk-card">
                <div className="brk-card__head">
                    <h2 className="brk-card__title">Latest leads</h2>
                    {leads.length > 0 && <button type="button" className="brk-link" onClick={() => onGo("leads")}>See all</button>}
                </div>
                {leads.length === 0 ? (
                    <p className="brk-empty">No leads yet. Enquiries, brochure downloads and tour bookings on your live listings show up here.</p>
                ) : (
                    <ul className="brk-list">
                        {leads.slice(0, 5).map((lead) => (
                            <li key={lead.id} className="brk-list__row">
                                <div>
                                    <span className="brk-list__title">{lead.name || "Buyer"}</span>
                                    <p className="brk-list__note">{LEAD_KIND[lead.kind]} · {lead.propertyTitle}</p>
                                </div>
                                {lead.mobile && <a className="brk-link" href={`tel:+91${lead.mobile}`}>Call {lead.mobile}</a>}
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
};

export default OverviewTab;
